import React, { useState } from 'react';

import {
    Modal,
    Button,
    Form,
    FormItemProps,
    Input,
    notification,
    Select,
    Typography,
    Radio,
    Checkbox,
    Upload,
    message,
} from 'antd';

import { ErrorRender, FormErrorParser, UploadFile } from '@partsbadger/library';

import { observer } from 'mobx-react';

import SalesOrderStore from '../../../stores/SalesOrderStore';

import PortalLoading from 'components/PortalLoading';
import { UploadOutlined } from '@ant-design/icons';

interface IProps {
    showRequestOrderAdjustment: boolean;
    onClose: () => void;
}

const { Title } = Typography;

const RequestOrderAdjustment: React.FC<IProps> = ({ showRequestOrderAdjustment, onClose }) => {
    const [loading, setLoading] = useState(false);

    const [form] = Form.useForm();

    const { order } = SalesOrderStore;

    const formItemLayout: FormItemProps = {
        labelCol: { span: 24 },
        wrapperCol: { span: 22 },
        labelAlign: 'left',
    };

    const handleRequestAdjustment = () => {
        setLoading(true);
        form.validateFields()
            .then(async values => {
                const payload = {
                    reason: values.reason,
                    details: values.details,
                    first_article_approval: order?.additional_requirements?.includes('First Article Approval')
                        ? true
                        : false,
                    approval: values.approval,
                    feedback: values.feedback,
                    file: values?.files ? values?.files[0]?.response : null,
                    rma: values?.rma_request,
                    design_change_request_files:
                        values?.design_change_request_files?.length > 0
                            ? values.design_change_request_files.map(
                                  (file: { response: { id: number; file: string; filename: string } }) => file.response
                              )
                            : null,
                };

                if (values?.details?.length < 5 || values?.details?.length > 200) {
                    notification.error({
                        message:
                            'Please note that the minimum character limit is 5, while the maximum allowed is 200 characters.',
                        placement: 'topRight',
                    });
                    setLoading(false);
                    return true;
                }

                if (
                    (order?.additional_requirements?.includes('First Article Approval') &&
                        !values.approval &&
                        values?.feedback?.length < 5) ||
                    values?.feedback?.length > 200
                ) {
                    notification.error({
                        message:
                            'Please note that the minimum character limit is 5, while the maximum allowed is 200 characters.',
                        placement: 'topRight',
                    });
                    setLoading(false);
                    return true;
                }

                if (order) {
                    await SalesOrderStore.requestOrderAdjustment(order?.id, payload)
                        .then(() => {
                            notification.success({
                                message: 'The team will reach out shortly with an update on your request.',
                                placement: 'topRight',
                            });

                            onClose();
                        })
                        .catch((err: any) => {
                            notification.error({
                                message: <ErrorRender error={err} />,
                                placement: 'topRight',
                            });
                        });
                }

                setLoading(false);
            })
            .catch(errorInfo => {
                setLoading(false);

                notification.error({
                    key: 'form-errors',
                    duration: 8,
                    message: 'Please input all required fields',
                    description: <FormErrorParser isAntd4 errors={errorInfo.errorFields} />,
                    placement: 'top',
                    maxCount: 1,
                });
            });
    };

    const selectOptions = [
        {
            value: 'Design Change Requests',
            label: 'Design Change Requests',
        },
        {
            value: 'Hold/Cancellation Requests',
            label: 'Hold/Cancellation Requests',
        },
        {
            value: 'Expedite Requests',
            label: 'Expedite Requests',
        },
        {
            value: 'Batch Date Change Requests',
            label: 'Batch Date Change Requests',
        },
    ];

    return (
        <div>
            <Modal
                open={showRequestOrderAdjustment}
                title="Request Order Adjustment"
                width={600}
                destroyOnClose={true}
                onCancel={() => onClose()}
                // bodyStyle={{ overflowY: 'scroll', overflowX: 'auto', height: '60vh' }}
                footer={[
                    <>
                        <div key={1} className="flex justify-between">
                            <Button
                                onClick={() => {
                                    onClose();
                                }}
                                size="large"
                                loading={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                size="large"
                                loading={loading}
                                onClick={() => {
                                    handleRequestAdjustment();
                                }}
                            >
                                Submit
                            </Button>
                        </div>
                    </>,
                ]}
            >
                {!order ? (
                    <div style={{ height: '600px' }}>
                        <PortalLoading />
                    </div>
                ) : (
                    <Form form={form}>
                        <div className="flex flex-col">
                            <p>
                                Please use this form to create a Order Adjustment Request. Your submission will be sent
                                to the Sales Representative Team.
                            </p>

                            <div>
                                <Form.Item
                                    name="reason"
                                    label="Reason for Order Adjustment Request"
                                    {...formItemLayout}
                                    rules={[{ required: true, message: 'Please, select one Reason' }]}
                                >
                                    <Select placeholder="Select one reason" options={selectOptions} />
                                </Form.Item>
                                <Form.Item noStyle dependencies={['reason']}>
                                    {({ getFieldValue }) => {
                                        return (
                                            <Form.Item
                                                label="Details"
                                                {...formItemLayout}
                                                name="details"
                                                rules={[{ required: true, message: 'Please input a note' }]}
                                                className="w-full"
                                            >
                                                <Input.TextArea
                                                    className="w-full"
                                                    minLength={5}
                                                    maxLength={200}
                                                    placeholder={
                                                        getFieldValue('reason') == 'Design Change Requests'
                                                            ? 'Please provide details regarding the requested design changes.'
                                                            : 'Please enter your order adjustment request'
                                                    }
                                                />
                                            </Form.Item>
                                        );
                                    }}
                                </Form.Item>
                                <Form.Item noStyle dependencies={['reason']}>
                                    {({ getFieldValue }) => {
                                        return (
                                            getFieldValue('reason') == 'Design Change Requests' && (
                                                <>
                                                    <Form.Item
                                                        label="File"
                                                        {...formItemLayout}
                                                        name="design_change_request_files"
                                                        rules={[{ required: true, message: 'Please input a file.' }]}
                                                        extra="Please note that we will require a new part file in order to evaluate your design change request."
                                                    >
                                                        <UploadFile multiple />
                                                    </Form.Item>
                                                </>
                                            )
                                        );
                                    }}
                                </Form.Item>
                            </div>

                            {order.additional_requirements?.includes('First Article Approval') && (
                                <div>
                                    <Title level={2}>Submit Sample Approval or Rejection</Title>
                                    <p>
                                        Based on the information provided, it appears that our production team has
                                        provided a First Article Approval Sample. Before proceeding with further steps,
                                        it is recommended to verify if the parts are suitable for full production.
                                    </p>

                                    <Form.Item
                                        label="Do you approve the First Article Sample?"
                                        {...formItemLayout}
                                        name="approval"
                                        rules={[
                                            { required: true, message: 'Please, select an option: Approve or Reject' },
                                        ]}
                                        className="w-full"
                                    >
                                        <Radio.Group>
                                            <Radio value={true}>Approve</Radio>
                                            <Radio value={false}>Reject</Radio>
                                        </Radio.Group>
                                    </Form.Item>

                                    <Form.Item
                                        dependencies={['approval']} // if using dependencies
                                        noStyle
                                    >
                                        {({ getFieldValue }) => {
                                            // getFieldValue defined here
                                            return (
                                                getFieldValue('approval') === false && (
                                                    <>
                                                        <Form.Item
                                                            label="RMA Request"
                                                            {...formItemLayout}
                                                            name="rma_request"
                                                            valuePropName="checked"
                                                            initialValue={false}
                                                        >
                                                            <Checkbox>Check if you want an RMA request</Checkbox>
                                                        </Form.Item>
                                                        <Form.Item
                                                            label="Upload an Inspection / Quality documentation"
                                                            {...formItemLayout}
                                                            name="files"
                                                            rules={[
                                                                { required: true, message: 'Please, input a file.' },
                                                            ]}
                                                        >
                                                            <UploadFile />
                                                        </Form.Item>
                                                        <Form.Item
                                                            label="Sample Rejection Feedback"
                                                            {...formItemLayout}
                                                            name="feedback"
                                                            rules={[
                                                                { required: true, message: 'Please, input a feedback' },
                                                            ]}
                                                            className="w-full"
                                                            help="Your Feedback will be sent to your Sales Representative"
                                                        >
                                                            <Input.TextArea
                                                                className="w-full"
                                                                minLength={5}
                                                                maxLength={200}
                                                                placeholder="Please enter your feedback"
                                                            />
                                                        </Form.Item>
                                                    </>
                                                )
                                            );
                                        }}
                                    </Form.Item>
                                </div>
                            )}
                        </div>
                    </Form>
                )}
            </Modal>
        </div>
    );
};

export default observer(RequestOrderAdjustment);
